import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Parse from "parse";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext";
import { useTheme } from "../../context/ThemeContext";
import {
  OutcomeDetails,
  OutcomesForParty,
} from "../../shared/Analytics";
import { ShownOutcome } from "../../shared/adjustedView";
import { ZERO } from "../../shared/bigUtil";
import { percentage } from "../../shared/percentage";
import { OfferStatus } from "../../shared/CaseModel";
import { Metric } from "../general/dataAtoms/Metric";
import { SectionLabel } from "../general/SectionLabel";
import {
  groupJudgmentsByNetResult,
  JudgmentsWithSameNetResult,
} from "./CaseOutcomeUtility";
import { OutcomeGroup, OutcomesByNetResult } from "./OutcomesByNetResult";
import {
  IssueOutcome,
  JudgementDetail,
  OutcomeSummaryDialog,
} from "./OutcomeSummaryDialog.tsx";

/**
 * What stands in for the dialog of the outcomes while they load, or where
 * loading failed, has the corners of that dialog.
 */
const WAITING_PAPER = { borderRadius: 3 } as const;

export const CaseOutcome = ({
  outcome,
  partyName,
}: {
  outcome: OutcomesForParty<ShownOutcome>;
  partyName: string;
}) => {
  const { theme } = useTheme();
  const judgmentsByNetResult: JudgmentsWithSameNetResult[] = useMemo(
    () => groupJudgmentsByNetResult(outcome),
    [outcome],
  );
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  // Heavy outcome details (ledger) are not stored — fetched on demand per
  // net-result group and cached by group identity (`selectedUuids`) so reopening
  // a group it already loaded needs no refetch. Each cache entry is keyed by
  // outcome uuid. The error is tracked by the group key it belongs to so a
  // cached group can't inherit another group's failure.
  const [detailsCache, setDetailsCache] = useState<
    Record<string, Record<string, OutcomeDetails>>
  >({});
  const [errorKey, setErrorKey] = useState<string>();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.outcomes",
  });
  const caseContext = useCase();
  const issues = caseContext.getIssueDescriptors();
  const parties = caseContext.getPartyDescriptors();
  const claimMatrix = caseContext.getClaimMatrix();
  const c = caseContext.getCase();

  const selectedGroup =
    selectedIndex !== null ? judgmentsByNetResult[selectedIndex] : undefined;
  const selectedUuids = selectedGroup?.judgments.map((j) => j.uuid).join(",");
  // Both derived from the current group key, so they can never carry another
  // group's bodies/error (the cache is per-key; the error is group-scoped).
  const details = selectedUuids ? detailsCache[selectedUuids] : undefined;
  const detailsError = errorKey !== undefined && errorKey === selectedUuids;
  // Only open the dialog once every judgment in the selected group has a loaded
  // body — otherwise show the spinner (also guards a partial server response).
  const detailsReady =
    selectedGroup?.judgments.every((j) => details?.[j.uuid] !== undefined) ??
    false;
  useEffect(() => {
    if (!dialogOpen || !selectedGroup || !selectedUuids) return;
    // Cache hit: a previously-loaded group reopened — no refetch, no spinner.
    if (detailsCache[selectedUuids]) return;
    let cancelled = false;
    const key = selectedUuids;
    setErrorKey(undefined);
    // an outcome of the adjusted results is rebuilt from its world
    const worldOf = new Map(
      outcome.outcomes.map((o) => [o.uuid, { world: o.world, scenario: o.scenario }]),
    );
    const adjusted = selectedGroup.judgments.some(
      ({ uuid }) => worldOf.get(uuid)?.world !== undefined,
    );
    (adjusted
      ? Promise.all(
          selectedGroup.judgments.map(async ({ uuid }) => [
            uuid,
            await Parse.Cloud.run("getAdjustedOutcomeDetails", {
              caseId: c.id,
              partyId: outcome.partyId,
              ...worldOf.get(uuid),
            }),
          ]),
        ).then(Object.fromEntries)
      : Parse.Cloud.run("getOutcomeDetails", {
          caseId: c.id,
          partyId: outcome.partyId,
          uuids: selectedGroup.judgments.map((j) => j.uuid),
        })
    )
      .then((res: Record<string, OutcomeDetails>) => {
        if (!cancelled) setDetailsCache((prev) => ({ ...prev, [key]: res }));
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn("Failed to load outcome details", e);
        setErrorKey(key);
      });
    return () => {
      cancelled = true;
    };
    // selectedUuids captures the group identity; c.id/partyId pin the case.
    // selectedGroup/detailsCache are intentionally read as live snapshots, not
    // deps — only a group/case change should re-run the fetch.
  }, [dialogOpen, selectedUuids, c.id, outcome.partyId]);
  // what the chart and the table draw of each group
  const groups: OutcomeGroup[] = useMemo(
    () =>
      judgmentsByNetResult.map(({ netResult, judgments, settlementStages }) => ({
        netResult,
        settlementStages,
        probability: judgments.reduce(
          (sum, judgement) => sum + judgement.probability,
          0,
        ),
        outcomes: judgments.length,
      })),
    [judgmentsByNetResult],
  );

  if (judgmentsByNetResult.length === 0)
    return (
      <Typography key="stage-label">
        {t("description", { count: 0 })}
      </Typography>
    );

  let negativeJudgments = 0;
  let negativeJudgmentsProbability = ZERO;
  let negativeNetOutcomes = 0;
  let negativeNetOutcomesProbability = ZERO;
  judgmentsByNetResult.forEach((judgmentGroup) => {
    judgmentGroup.judgments.forEach(({ judgementValue, probability }) => {
      if (judgementValue < 0) {
        negativeJudgments++;
        negativeJudgmentsProbability =
          negativeJudgmentsProbability.add(probability);
      }
      if (judgmentGroup.netResult < 0) {
        negativeNetOutcomes++;
        negativeNetOutcomesProbability =
          negativeNetOutcomesProbability.add(probability);
      }
    });
  });

  const allOutcomesNegative = groups.every(({ netResult }) => netResult < 0);

  function openDialog(
    judgmentGroup: JudgmentsWithSameNetResult,
    loadedDetails: Record<string, OutcomeDetails>,
  ) {
    const probabilityTotalOfAllJudgments = judgmentGroup.judgments.reduce(
      (previous, { probability }) => previous + probability,
      0,
    );
    const judgments: JudgementDetail[] = judgmentGroup.judgments.map(
      (judgment) => {
        const {
          issuesWon,
          issuesLost,
          netResult,
          probability,
          judgementValue,
          enforcement,
        } = judgment;

        const issueOutcomeMap = new Map<string, IssueOutcome>();

        const addIssueOutcome = (
          issueId: string,
          status: "won" | "lost" | "settled",
          names: string[],
          settlementAmountBalance: number = 0,
        ) => {
          if (names.length === 0) return;

          const issue = issues[issueId];
          if (!issue) return;

          let outcome: IssueOutcome | undefined = issueOutcomeMap.get(issueId);
          if (!outcome) {
            outcome = {
              issueId,
              title: issue.title,
              opponentStatuses: [],
            };
            issueOutcomeMap.set(issueId, outcome);
          }

          if (status === "settled")
            outcome.opponentStatuses.push({
              status: "settled",
              names,
              settlementAmountBalance,
            });
          else outcome.opponentStatuses.push({ status, names });
        };

        const getOpponentNames = (issueId: string) =>
          claimMatrix
            .getOpponentsInClaim(outcome.partyId, issueId)
            .map((id) => parties[id]?.name ?? id);

        // won issues
        issuesWon?.forEach((issueId) => {
          addIssueOutcome(issueId, "won", getOpponentNames(issueId));
        });

        // lost issues
        issuesLost?.forEach((issueId) => {
          addIssueOutcome(issueId, "lost", getOpponentNames(issueId));
        });

        //settled issues and the amount settled for
        outcome.issuesSettled.forEach((issueId) => {
          // settlements that are accepted
          const acceptedSettlements =
            c.settlements?.filter(
              ({ status, settledClaims }) =>
                status === OfferStatus.Accepted &&
                settledClaims.includes(issueId),
            ) ?? [];

          // aggregate if multiple settlements are accepted
          const settlementBalance = acceptedSettlements.reduce(
            (previous, { payment: { amount, offereePays }, offereeId }) => {
              if (offereeId === outcome.partyId) {
                // party is offeree
                if (offereePays)
                  // party (offeree) pays
                  return previous - amount;
                // party (offeree) gets paid
                return previous + amount;
              }
              // party is offeror
              if (offereePays)
                // party (offeror) gets paid
                return previous + amount;
              // party (offeror) pays
              return previous - amount;
            },
            0,
          );

          addIssueOutcome(
            issueId,
            "settled",
            getOpponentNames(issueId),
            settlementBalance,
          );
        });

        const issueOutcomes = Array.from(issueOutcomeMap.values());

        // settlement status
        let settlementStatus: string[];

        const settledIssues = issueOutcomes.filter(({ opponentStatuses }) =>
          opponentStatuses.some(({ status }) => status === "settled"),
        );
        const settledCount = settledIssues.length;

        if (settledCount === 0) {
          settlementStatus = [t("outcomeDialog.settlementStatus.none")];
        } else if (
          settledCount > 0 &&
          issuesLost.length === 0 &&
          issuesWon.length === 0
        ) {
          settlementStatus = [t("outcomeDialog.settlementStatus.full")];
        } else {
          // Only the counterparties on actually-accepted settlements (the
          // settled-status opponentStatus carries all opponents in the claim
          // — that's correct for the table row but wrong for this chip text).
          const allOpponents = new Set<string>();
          (c.settlements ?? [])
            .filter(
              ({ status, settledClaims }) =>
                status === OfferStatus.Accepted &&
                settledClaims.some((id) => outcome.issuesSettled.includes(id)),
            )
            .forEach(({ offereeId, offerorId }) => {
              const opponentId =
                offereeId === outcome.partyId ? offerorId : offereeId;
              allOpponents.add(parties[opponentId]?.name ?? opponentId);
            });

          settlementStatus = [
            t("outcomeDialog.settlementStatus.partial", {
              claims: settledIssues.map(({ title }) => title).join(", "),
              opponents: Array.from(allOpponents).join(", "),
            }),
          ];
        }

        // agreements
        const partyAgreements = c.agreements?.find(
          ({ partyId }) => partyId === outcome.partyId,
        );
        const hasAgreements = {
          lawFirm: partyAgreements?.lawFirmCfa?.active === true,
          barrister: partyAgreements?.barristerCfa?.active === true,
        };

        return {
          issueOutcomes,
          settlementStatus,
          enforcement,
          judgementValue,
          probability,
          netResult,
          hasAgreements,
          ledger: loadedDetails[judgment.uuid].ledger,
          // dev-only ledger DAG view: identifies this outcome for the
          // on-demand recompute (the dialog offers it to the users the
          // decisionTree toggle is on for)
          dag: {
            caseId: c.id,
            partyId: outcome.partyId,
            outcomeIndex: judgment.outcomeIndex,
          },
        };
      },
    );
    return (
      <OutcomeSummaryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        judgments={judgments.sort((j1, j2) => j2.probability - j1.probability)}
        probability={probabilityTotalOfAllJudgments}
        partyName={partyName}
        isClient={parties[outcome.partyId]?.isClient === true}
      />
    );
  }

  const metrics: { label: string; value: string; isAdverse?: boolean }[] = [
    {
      label: t("summary.judgments.total"),
      value: String(outcome.outcomes.length),
    },
    {
      label: t("summary.judgments.negative"),
      value: String(negativeJudgments),
    },
    {
      label: t("summary.judgments.negativeProbability"),
      value: percentage.format(negativeJudgmentsProbability.toNumber()),
    },
    {
      label: t("summary.outcomes.total"),
      value: String(judgmentsByNetResult.length),
    },
    {
      label: t("summary.outcomes.negative"),
      value: String(negativeNetOutcomes),
    },
    {
      label: t("summary.outcomes.negativeProbability"),
      value: percentage.format(negativeNetOutcomesProbability.toNumber()),
      // the one figure of the six that is a risk to the party's money
      isAdverse: negativeNetOutcomesProbability.gt(0),
    },
  ];

  return (
    <Stack spacing={2.25} sx={{ userSelect: "none" }}>
      <Paper
        variant="outlined"
        sx={{ borderRadius: 3, px: 2.5, pt: 2, pb: 2.5 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box>
            <SectionLabel component="h2">{t("summary.title")}</SectionLabel>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, color: "text.secondary" }}
            >
              {t("description", { name: partyName })}
            </Typography>
          </Box>
          {allOutcomesNegative ? (
            <Alert severity="error" sx={{ py: 0 }}>
              {t("summary.alerts.allNegative", { name: partyName })}
            </Alert>
          ) : (
            negativeNetOutcomesProbability.gt(0.5) && (
              <Alert severity="warning" sx={{ py: 0 }}>
                {t("summary.alerts.mostNegative", {
                  name: partyName,
                  probability: percentage.format(
                    negativeNetOutcomesProbability.toNumber(),
                  ),
                })}
              </Alert>
            )
          )}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            columnGap: 3.5,
            rowGap: 2.25,
            mt: 2,
          }}
        >
          {metrics.map(({ label, value, isAdverse }) => (
            <Metric
              key={label}
              label={label}
              value={value}
              tone={isAdverse ? "adverse" : undefined}
            />
          ))}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ px: 2.5, pt: 2 }}>
          <SectionLabel component="h2">
            {t("chart.netResults.title")}
          </SectionLabel>
          <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
            {t("judgments.description")}
          </Typography>
        </Box>
        <OutcomesByNetResult
          groups={groups}
          onOpen={(index) => {
            setSelectedIndex(index);
            setDialogOpen(true);
          }}
        />
      </Paper>
      {dialogOpen &&
        selectedGroup &&
        (detailsError ? (
          <Dialog
            open
            onClose={() => setDialogOpen(false)}
            slotProps={{ paper: { sx: WAITING_PAPER } }}
          >
            <DialogContent>
              <Alert severity="error">{t("detailsError")}</Alert>
            </DialogContent>
          </Dialog>
        ) : details && detailsReady ? (
          openDialog(selectedGroup, details)
        ) : (
          <Dialog
            open
            onClose={() => setDialogOpen(false)}
            slotProps={{ paper: { sx: WAITING_PAPER } }}
          >
            <DialogContent
              sx={{
                display: "flex",
                justifyContent: "center",
                p: theme.spacing(4),
              }}
            >
              <CircularProgress />
            </DialogContent>
          </Dialog>
        ))}
    </Stack>
  );
};
